/*

Sainapse® incorporates patented, and “patent pending”, technology.

© 2017 - 2024 Sainapse Inc. and its licensors. All rights reserved worldwide.

Sainapse Inc. and its licensors own all rights in this work, including but not limited to the source code, algorithms, text, 
flowcharts and images herein, which are protected by copyright laws in applicable jurisdictions. Any use of this work is permissible 
only with an express written license or authorization from Sainapse Inc., and only to the extent and for the purpose permitted. 
Unauthorized copying, editing, reproduction, redistribution, modification, creation of derivative works, translation, adaptation, 
customization, decompilation, reverse engineering, disassembling or hacking of this work or any part thereof, including but not limited to the source code, 
header files and libraries of the software, via any medium is strictly prohibited.

Open Source Software that is provided as part of Sainapse® is subject to and governed solely by the terms and conditions of the applicable open source license agreements, 
and their respective terms, conditions, restrictions and obligations, a copy of which will be made available to you upon request.

Proprietary and Confidential. Sainapse® and associated documentation is the property of Sainapse Inc. and considered confidential information. 
No part of it may be disclosed without the express written consent of Sainapse Inc.Please note that any infringement or violation will invite legal action, 
both civil and criminal, as appropriate, without any further notice.

*/
import React from "react";
import Row from "./Row";
import {
  Typography,
  TextField,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";
import Actions from "./../../../../../../fluid/Actions/ActionFactory";
import ErrorMessageAlert from "../../../../../Utilities/ErrorMessageAlert";
import { withStyles } from "@material-ui/core/styles";
const useStyles = (theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
});

function getMockCell({
  rowIndex = 0,
  colIndex = 0,
  value = "",
  backgroundColor = "#ffffff",
  fontColor = "#000000",
  border = "1px solid #D4D4D4",
  borderTop = "",
  borderRight = "1px ",
  borderBottom = "",
  borderLeft = "",
  width,
  height,
  colSpan = 1,
  rowSpan = 1,
}) {
  return {
    col_index: colIndex,
    row_index: rowIndex,
    style: {
      width: width,
      height: height,
      background_color: backgroundColor,
      font_color: fontColor,
      border_bottom: borderBottom || "NONE",
      border_left: borderLeft || "NONE",
      border_top: borderTop || "NONE",
      border_right: borderRight || "NONE",
      border: border,
      bold: false,
      align: "center",
      wrap: false,
      italic: false,
    },
    value: value,
    col_span: colSpan,
    row_span: rowSpan,
  };
}
//ascii vlaue of A
const asciiValueOfA = "A".charCodeAt(0);
const asciiValueOfZ = "Z".charCodeAt(0);
const aplphbetLength = 26;

//colindx ascii number from string
//treat 'A' as 1
function colIndxStringToColNo(colIndxString) {
  //remove spaces
  colIndxString = colIndxString.trim();
  let multiplier = 1; // 26 power 0 iniialzed

  let colNumber = 0;
  //start from last character in passed colINdx string
  for (let char of colIndxString.split("").reverse()) {
    //check if its valid character
    let charAsciiValue = char.charCodeAt(0);
    if (charAsciiValue < asciiValueOfA || charAsciiValue > asciiValueOfZ) {
      throw new Error("invalid col_indx passed ");
    }

    colNumber = colNumber + multiplier * (charAsciiValue - asciiValueOfA + 1);
    multiplier = multiplier * aplphbetLength;
  }
  return colNumber;
}
//treat 1 as 'A'
function colNoToColIndxString(colNo) {
  if (colNo === 0) return "0";
  let colIndxString = "";
  while (colNo > aplphbetLength) {
    colIndxString =
      String.fromCharCode((colNo % aplphbetLength) + asciiValueOfA - 1) +
      colIndxString;

    colNo = Math.floor(colNo / aplphbetLength);
  }
  return String.fromCharCode(colNo + asciiValueOfA - 1) + colIndxString;
}
//add row indexing
function addRowSequenceNos(rowsData) {
  if (!rowsData) return;
  let sequenceAddedRowsData;
  //this variable assist in adding row index for records without row_index or empty row records
  let lastProvidedRowIndex = 1;
  sequenceAddedRowsData = rowsData.map(function (singleRowData) {
    //read row index from first cell and assume it's same for entire row
    let sequenceNo;
    if (singleRowData[0]) {
      sequenceNo = singleRowData[0].row_index;
      //store last provieded row_index
      lastProvidedRowIndex = sequenceNo;
    } else {
      sequenceNo = lastProvidedRowIndex + 1;
    }
    return [
      getMockCell({
        rowIndex: sequenceNo,
        colIndex: 0,
        value: sequenceNo,
        backgroundColor: "#EDEBE9",
        fontColor: "#696968",
        border: "1px solid #D4D4D4",
      }),
      ...singleRowData,
    ];
  });
  return sequenceAddedRowsData;
}
//add column indexing
function getColumnIndexArray(totalCols, row_index = 0) {
  let colIndexArray = [];
  //inital guy is (0,0)
  colIndexArray[0] = getMockCell({
    rowIndex: row_index,
    colIndex: 0,
    value: "",
    backgroundColor: "#EDEBE9",
    fontColor: "#696968",
  });
  for (let colNo = 1; colNo < totalCols; colNo++) {
    colIndexArray[colNo] = getMockCell({
      rowIndex: row_index,
      colIndex: colNoToColIndxString(colNo),
      value: colNoToColIndxString(colNo),
      backgroundColor: "#EDEBE9",
      fontColor: "#696968",
      border: "1px solid #D4D4D4",
    });
  }
  return colIndexArray;
}

function updateColIndex(dataRow) {
  dataRow.forEach((cell, index) => {
    if (cell.col_index === null) {
      cell.col_index = colNoToColIndxString(index);
    }
  });
}

class Sheet extends React.Component {
  constructor(props) {
    super(props);
    this.handleScroll = this.handleScroll.bind(this);
    this.debounce = this.debounce.bind(this);

    this.scrollRef = React.createRef();

    this.state = {
      startingRow: 0,
      endingRow: 0,
      minRowNo: Number.NEGATIVE_INFINITY,
      maxRowNo: Number.POSITIVE_INFINITY,
      rows: [],
      columnIndexesRow: [],
      columnNamesRow: [],
      MAX_NUM_ROWS_TO_FETCH_LAZILY_ON_SCROLL: 50,
      MAX_WINDOW_SIZE: 100,
      thresholdPixels: 5,
      scrollPrevY: 0,
      loading: false,
      loadingText: "",
      loadedUp: false,
      errorOpen: false,
      errorInfo: {},
    };
  }

  componentDidMount() {
    this.setState({
      loading: true,
      loadingText: "Loading selected record...",
    });
    // this.props.setLoadingState(true);
    this.loadInitialRows();
  }

  componentDidUpdate(prevProps) {
    // if (this.state.loadedUp) {
    //   console.log("loaded up!");
    //   this.scrollRef.current.scrollTop = 500;
    //   this.setState({
    //     loadedUp: false,
    //   });
    // }

    this.handleUpdatedActiveRowNo(prevProps);
  }

  loadInitialRows = async () => {
    try {
      let {
        activeRowNo,
        fileLink,
        workSheets,
        selectedWorkSheetName,
        drlUrl,
      } = this.props;

      //for arthimetic reasons convert and make sure it is a number.
      activeRowNo = Number(activeRowNo);
      if (isNaN(activeRowNo)) {
        throw new Error("invalid active row number format.");
      }
      //get sleected worksheet
      let selectedWorkSheet = workSheets.filter(
        (workSheet) => workSheet.name === selectedWorkSheetName
      );
      //take first sheet matching the name
      selectedWorkSheet = selectedWorkSheet[0];
      const { max_row, max_columns } = selectedWorkSheet;

      //assume as 2, 1 being header row
      let minRowNo = 1;
      let maxRowNo = max_row;
      // maxRowNo = max_row

      if (activeRowNo < minRowNo || activeRowNo > maxRowNo) {
        throw new Error("invalid active row no");
      }
      const { MAX_WINDOW_SIZE } = this.state;

      let strtRow = Math.max(
        minRowNo,
        activeRowNo - Math.floor(MAX_WINDOW_SIZE / 2)
      );

      let endRow = Math.min(
        activeRowNo + Math.floor(MAX_WINDOW_SIZE / 2),
        maxRowNo
      );

      // api call for initial set of data with header_required included
      let res = await Actions("http_request", {
        url: `${drlUrl}/drl/get_table_row_range`,
        method: "get",
        params: {
          file_link: fileLink,
          work_sheet_name: selectedWorkSheetName,
          start_row: strtRow,
          end_row: endRow,
          include_headers: true,
          header_row: 1,
          file_type: "csv",
        },
      });

      let data = res.data.data;

      //add sequence numbering to each row
      data = addRowSequenceNos(data);
      // //add col_index values as csv don't have them.
      // data.forEach((dataRow) => {
      //   updateColIndex(dataRow);
      // });
      //first row is column header row.
      let columnNamesRow = data[0];
      data.shift();

      //for csv we assume column names row is the largest row.
      let totalCols = columnNamesRow.length;
      let columnIndexesRow = getColumnIndexArray(totalCols, 0);
      //initialize state variables necessary for initial render.
      // this.props.setLoadingState(false);
      this.setState(
        {
          startingRow: strtRow,
          endingRow: endRow,
          rows: data,
          loadingText: "",
          loading: false,
          minRowNo: minRowNo,
          maxRowNo: maxRowNo,
          columnIndexesRow: columnIndexesRow,
          columnNamesRow: columnNamesRow,
        },
        () => {
          this.addResizing();
        }
      );
    } catch (err) {
      // this.props.setLoadingState(false);
      this.setState({
        errorInfo: err,
        errorOpen: true,
        loadingText: "",
        loading: false,
      });
      console.log("error during mounting of sheet", err);
    }
  };
  handleUpdatedActiveRowNo = async (prevProps) => {
    try {
      if (
        this.props.activeRowNo !== null &&
        prevProps.activeRowNo !== this.props.activeRowNo
      ) {
        let {
          activeRowNo,
          fileLink,
          selectedWorkSheetName,
          drlUrl,
          active,
        } = this.props;
        //for arthimetic reasons convert and make sure it is a number.
        activeRowNo = Number(activeRowNo);
        if (isNaN(activeRowNo)) {
          throw new Error("invalid active row number format.");
        }
        //check if the active row no is in range
        if (
          activeRowNo < this.state.startingRow ||
          activeRowNo > this.state.endingRow
        ) {
          this.setState({
            loading: true,
            loadingText: "Loading selected record...",
          });
          // this.props.setLoadingState(true);
          //assume as 2, 1 being header row
          let minRowNo = this.state.minRowNo;
          let maxRowNo = this.state.maxRowNo;
          // maxRowNo = max_row

          const { MAX_WINDOW_SIZE } = this.state;

          let strtRow = Math.max(
            minRowNo,
            activeRowNo - Math.floor(MAX_WINDOW_SIZE / 2)
          );

          let endRow = Math.min(
            activeRowNo + Math.floor(MAX_WINDOW_SIZE / 2),
            maxRowNo
          );

          let res = await Actions("http_request", {
            url: `${drlUrl}/drl/get_table_row_range`,
            method: "get",
            params: {
              file_link: fileLink,
              work_sheet_name: selectedWorkSheetName,
              start_row: strtRow,
              end_row: endRow,
              include_headers: false,
              header_row: 1,
              file_type: "csv",
            },
          });

          let data = res.data.data;
          data = addRowSequenceNos(data);
          // this.props.setLoadingState(true);
          this.setState({
            loading: false,
            loadingText: "",
            rows: data,
            startingRow: strtRow,
            endingRow: endRow,
          });
        }
      }
    } catch (err) {
      console.log("dkljfalksjskd ", err);
      // this.props.setLoadingState(false);
      this.setState({
        errorInfo: err,
        errorOpen: true,
        loadingText: "",
        loading: false,
      });
      console.log("error during updating sheet with new active row no", err);
    }
  };
  addResizing = () => {
    let pageHeight = this.scrollRef.current.scrollHeight;
    let scrollInitialY = Math.floor(pageHeight / 2) - 150;
    this.setState({ scrollPrevY: scrollInitialY });

    //handle expandable columns. closure is needed**
    var table = document.getElementById("csv-table");
    resizableGrid(table);
    function resizableGrid(table) {
      var row = table.getElementsByTagName("tr")[0];
      let cols = row ? row.children : undefined;
      if (!cols) return;
      for (var i = 0; i < cols.length; i++) {
        var div = createDiv(table.offsetHeight);
        cols[i].appendChild(div);
        // cols[i].style.position = "relative";
        setListeners(div);
      }
    }
    function createDiv(height) {
      var div = document.createElement("div");
      div.style.top = 0;
      div.style.right = "-2px";
      div.style.width = "4px";
      div.style.position = "absolute";
      div.style.cursor = "col-resize";
      /* remove backGroundColor later */
      // div.style.backgroundColor = "inherit";
      div.style.opacity = 0;
      div.style.userSelect = "none";
      /* table height */
      div.style.height = height + "px";
      return div;
    }

    function setListeners(div) {
      var pageX, curCol, nxtCol, curColWidth, nxtColWidth;
      div.addEventListener("mousedown", function (e) {
        curCol = e.target.parentElement;
        nxtCol = curCol.nextElementSibling;
        pageX = e.pageX;
        curColWidth = curCol.offsetWidth;
        if (nxtCol) nxtColWidth = nxtCol.offsetWidth;
      });

      document.addEventListener("mousemove", function (e) {
        if (curCol) {
          var diffX = e.pageX - pageX;

          if (nxtCol) nxtCol.style.minWidth = nxtColWidth - diffX + "px";

          curCol.style.minWidth = curColWidth + diffX + "px";
        }
      });

      document.addEventListener("mouseup", function (e) {
        curCol = undefined;
        nxtCol = undefined;
        pageX = undefined;
        nxtColWidth = undefined;
        curColWidth = undefined;
      });
    }
  };
  async handleScroll(scrollCrntY, pageHeight, clientHeight) {
    try {
      // return;
      let scrollOffset = scrollCrntY - this.state.scrollPrevY;

      this.setState({ scrollPrevY: scrollCrntY });
      const {
        activeRowNo,
        fileLink,
        workSheets,
        selectedWorkSheetName,
        drlUrl,
      } = this.props;

      const {
        minRowNo,
        maxRowNo,
        startingRow,
        endingRow,
        MAX_WINDOW_SIZE,
        rows,
        MAX_NUM_ROWS_TO_FETCH_LAZILY_ON_SCROLL,
        thresholdPixels,
      } = this.state;
      //fetch above records
      if (scrollOffset < 0) {
        if (startingRow <= minRowNo) return;
        if (scrollCrntY <= thresholdPixels) {
          // this.props.setLoadingState(true);
          this.setState({
            loading: true,
            loadingText: "Loading above records...",
          });
          //verify boundary
          let strtRow =
            startingRow - Math.floor(MAX_NUM_ROWS_TO_FETCH_LAZILY_ON_SCROLL);
          if (strtRow < minRowNo) {
            strtRow = minRowNo;
          }

          let endRow = startingRow - 1;
          if (endRow < minRowNo) {
            endRow = minRowNo;
          }

          let res = await Actions("http_request", {
            url: `${drlUrl}/drl/get_table_row_range`,
            method: "get",
            params: {
              file_link: fileLink,
              work_sheet_name: selectedWorkSheetName,
              start_row: strtRow,
              end_row: endRow,
              include_headers: false,
              header_row: 1,
              file_type: "csv",
            },
          });

          let data = res.data.data;
          data = addRowSequenceNos(data);
          //check for insufficient rows to display.if requested rows are less than threshold,
          // that is half the windowsize as per current design, we will fill those with existing rows in sequential order.
          let rowGap = MAX_WINDOW_SIZE - (endRow - strtRow + 1);
          if (rowGap > 0) {
            data = [...data, ...rows.slice(0, rowGap)];
          }
          // this.props.setLoadingState(false);
          this.setState({
            loadedUp: true,
            loading: false,
            loadingText: "",
            rows: data,
            startingRow: strtRow,
            endingRow: endRow + rowGap,
          });
        }
      }
      //fetch below records
      else {
        if (endingRow >= maxRowNo) return;
        if (pageHeight - (clientHeight + scrollCrntY) <= thresholdPixels) {
          // this.props.setLoadingState(true);
          this.setState({
            loading: true,
            loadingText: "Loading below records...",
          });
          let strtRow = endingRow + 1;
          if (strtRow > maxRowNo) {
            strtRow = maxRowNo;
          }
          let endRow =
            endingRow + Math.floor(MAX_NUM_ROWS_TO_FETCH_LAZILY_ON_SCROLL);
          if (endRow > maxRowNo) {
            endRow = maxRowNo;
          }
          let res = await Actions("http_request", {
            url: `${drlUrl}/drl/get_table_row_range`,
            method: "get",
            params: {
              file_link: fileLink,
              work_sheet_name: selectedWorkSheetName,
              start_row: strtRow,
              end_row: endRow,
              include_headers: false,
              header_row: 1,
              file_type: "csv",
            },
          });

          let data = res.data.data;
          data = addRowSequenceNos(data);
          //check for insufficient rows to display.if requested rows are less than threshold,
          // that is half the windowsize as per current design, we will fill those with existing rows in sequential order.
          let rowGap = MAX_WINDOW_SIZE - (endRow - strtRow + 1);
          if (rowGap > 0) {
            data = [...rows.slice(-rowGap), ...data];
          }
          // this.props.setLoadingState(false);

          this.setState({
            loading: false,
            loadingText: "",
            rows: data,
            startingRow: strtRow - rowGap,
            endingRow: endRow,
          });
        }
      }
    } catch (err) {
      // this.props.setLoadingState(false);
      this.setState({
        errorInfo: err,
        errorOpen: true,
        loading: false,
        loadingText: "",
      });
      console.log("error while handling scroll", err);
    }
  }

  debounce(handler, time) {
    let timer;

    return function (event) {
      let scrollTop = event.target.scrollTop;
      let pageHeight = event.target.scrollHeight;
      let clientHeight = event.target.clientHeight;
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        handler(scrollTop, pageHeight, clientHeight);
      }, time);
    };
  }

  handleErrorClose = () => {
    this.setState({
      errorOpen: false,
    });
  };

  renderLoader = () => {
    console.log("rendering loader", this.state.loading === true);
    if (this.state.loading === true) {
      //get height of
      let cellFormulaCont = document.getElementById(
        "cell-indices-formula-value-container"
      );
      let height = cellFormulaCont.style.height;
      return (
        <>
          <div
            classes="overlay-dialog"
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: height,
              left: 0,
              backgroundColor: "#000",
              opacity: 0.5,
              zIndex: 10,
            }}
          ></div>
          <Typography
            style={{
              color: "white",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(-50%, -50%)`,
              zIndex: 10,
              opacity: 1,
            }}
          >
            {this.state.loadingText}{" "}
          </Typography>
          <CircularProgress
            size={30}
            style={{
              position: "absolute",
              top: "54%",
              left: "50%",
              zIndex: 10,
              color: "white",
            }}
          />
        </>
      );
    } else return "";
  };
  render() {
    const {
      updateActiveCell,
      activeCell,
      activeRowNo,
      updateActiveRowNo,
      containerHeight,
      classes,
    } = this.props;

    console.log("loading", this.state.loading);
    return (
      <table
        id="csv-table"
        style={{
          width: "100%",
          height: "100%",
          tableLayout: "fixed",
          display: "block",
          borderCollapse: "seperate",
          borderSpacing: 0,
          fontSize: "11pt",
          overflowY: "scroll",
          flex: 1,
        }}
        onScroll={this.debounce(this.handleScroll, 100)}
        ref={this.scrollRef}
        className="sheet"
      >
        {this.renderLoader()}

        {[
          this.state.columnIndexesRow,
          this.state.columnNamesRow,
          ...this.state.rows,
        ].map(function (rowData) {
          if (rowData.length <= 0) {
            return null;
          } else {
            return (
              <Row
                key={rowData[0].row_index}
                containerHeight={containerHeight}
                rowData={rowData}
                isActive={rowData[0].row_index == activeRowNo}
                updateActiveRowNo={updateActiveRowNo}
                activeRowNo={activeRowNo}
                updateActiveCell={updateActiveCell}
                activeCell={activeCell}
              ></Row>
            );
          }
        })}
        <ErrorMessageAlert
          open={this.state.errorOpen}
          handleClose={this.handleErrorClose}
          errorInfo={this.state.errorInfo}
        ></ErrorMessageAlert>
      </table>
    );
  }
}

export default withStyles(useStyles)(Sheet);
